/**
 * oidc.ts — hand-rolled OIDC Authorization Code + PKCE (S256) client for the
 * broker SPA (Story ADMIN-W4, D-W4-OIDC-LIB).
 *
 * Zero dependencies by decision: we need exactly authorize-redirect + S256
 * challenge + code exchange + refresh + end-session against ONE known IdP
 * (Keycloak realm `brokly`, public client `brokly-web`); Web Crypto covers the
 * hashing. The SPA never VERIFIES tokens — it only carries them; the api
 * server-verifies every request (rule #2). ID-token claims are decoded here for
 * DISPLAY ONLY (D-W4-DISPLAY-CLAIMS).
 *
 * 2FA (AC 7): the realm has the CONFIGURE_TOTP required action enabled — on
 * FIRST login Keycloak itself hosts the TOTP setup (QR + code) inside the
 * authorize redirect, before it returns to /auth/callback. No custom 2FA UI.
 * Every authorize sends acr_values=two-factor to demand LoA 2 from the
 * brokly-browser step-up flow (A3): Keycloak rejects `default.acr.values` on
 * brokly-web, so the realm cannot demand it for us and dropping the parameter
 * silently reduces every login to one factor.
 *
 * A2-2 — SSO BYPASSES THIS 2FA (Stefan-accepted, Story WAVE2-SSO): when a broker
 * takes the "Mit Microsoft anmelden" button on the Keycloak login, the login is
 * BROKERED to Entra. Keycloak's Identity Provider Redirector is an ALTERNATIVE in
 * the brokly-browser flow, so a completed IdP login satisfies the flow and the
 * local forms subflow (password @ LoA1 + TOTP @ LoA2) never runs. The
 * acr_values=two-factor sent below is VOLUNTARY (not an essential claims request),
 * so Keycloak does not hard-fail when LoA 2 is not reached locally — SSO users
 * pass with the IdP's own factors. This SPA is unchanged for SSO: it always sends
 * the same authorize request; the branch happens entirely inside Keycloak. SSO
 * users' second factor is Entra's responsibility. See docs/how-to/sso-entra-setup.md.
 *
 * Flow state (verifier + state + intended path) lives in sessionStorage for the
 * duration of the redirect round-trip and is consumed exactly once.
 *
 * [Source: docs/stories/admin-w4-broker-auth.md Task 6]
 * [Source: apps/web/src/admin/lib/admin-auth.ts] (token-endpoint conventions)
 */

import { runtimeConfig } from '@runtime-config';

/** Keycloak realm base, e.g. https://auth.dev.brokly.ch/realms/brokly (no trailing slash). */
const OIDC_AUTHORITY: string =
  runtimeConfig('VITE_OIDC_AUTHORITY') ?? 'http://localhost:8080/realms/brokly';

/** The public broker SPA client (PKCE S256; redirect URIs via bootstrap-realm.mjs). */
const OIDC_CLIENT_ID: string = runtimeConfig('VITE_OIDC_CLIENT_ID') ?? 'brokly-web';

/** sessionStorage key of the in-flight authorize round-trip state. */
export const OIDC_FLOW_STORAGE_KEY = 'brokly-oidc-flow';

/** ACR demanding password + TOTP (realm acr.loa.map → LoA 2). */
const OIDC_ACR_VALUE = 'two-factor';

/** Skew subtracted from expires_in (clock drift + latency headroom). */
export const OIDC_TOKEN_EXPIRY_SKEW_SECONDS = 30;

/** A failed OIDC HTTP call (status only — never token material in messages). */
export class OidcError extends Error {
  constructor(public readonly status: number) {
    super(`OIDC request failed (HTTP ${status})`);
    this.name = 'OidcError';
  }
}

/** The token set a successful exchange/refresh yields (expiresAt = epoch ms). */
export interface OidcTokenSet {
  accessToken: string;
  refreshToken: string;
  idToken: string;
  /** Epoch ms after which the access token counts as expired (skew applied). */
  expiresAt: number;
}

/** Display-only identity claims decoded from the ID token (never authorization). */
export interface IdTokenDisplayClaims {
  name: string | null;
  email: string | null;
  preferredUsername: string | null;
}

/** The persisted authorize round-trip state. */
interface OidcFlowState {
  verifier: string;
  state: string;
  redirectPath: string;
}

// ── endpoints ─────────────────────────────────────────────────────────────────

function authorizeEndpoint(): string {
  return `${OIDC_AUTHORITY}/protocol/openid-connect/auth`;
}

function tokenEndpoint(): string {
  return `${OIDC_AUTHORITY}/protocol/openid-connect/token`;
}

function endSessionEndpoint(): string {
  return `${OIDC_AUTHORITY}/protocol/openid-connect/logout`;
}

/** The SPA's registered redirect URI (route /auth/callback on this origin). */
export function redirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

// ── PKCE primitives (Web Crypto) ──────────────────────────────────────────────

/** base64url (no padding) of raw bytes. */
function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Crypto-random base64url string of `byteLength` random bytes. */
export function randomUrlSafe(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** S256 code challenge: BASE64URL(SHA-256(ASCII(verifier))) — RFC 7636 §4.2. */
export async function s256Challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

// ── flow-state storage ────────────────────────────────────────────────────────

function storeFlowState(flow: OidcFlowState): void {
  window.sessionStorage.setItem(OIDC_FLOW_STORAGE_KEY, JSON.stringify(flow));
}

/** Read AND clear the in-flight flow state (consumed exactly once). */
export function consumeFlowState(): OidcFlowState | null {
  const raw = window.sessionStorage.getItem(OIDC_FLOW_STORAGE_KEY);
  window.sessionStorage.removeItem(OIDC_FLOW_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as OidcFlowState;
    return parsed?.verifier && parsed?.state ? parsed : null;
  } catch {
    return null;
  }
}

// ── authorize URL ─────────────────────────────────────────────────────────────

/** Keycloak IdP alias for the Microsoft/Entra broker (WAVE2-SSO). */
export const OIDC_IDP_MICROSOFT = 'microsoft' as const;

/**
 * Build the authorize URL and persist {verifier, state, redirectPath} for the
 * callback. The caller assigns window.location to the returned URL.
 *
 * `idpHint` (WAVE2-SSO) → adds `kc_idp_hint`, so Keycloak jumps STRAIGHT to that
 * identity provider instead of rendering its own login page. Used by the SSO
 * activation path to go directly to Microsoft after a passwordless activation.
 */
export async function buildAuthorizationUrl(
  redirectPath: string = '/',
  idpHint?: string,
): Promise<string> {
  const verifier = randomUrlSafe(32);
  const state = randomUrlSafe(16);
  storeFlowState({ verifier, state, redirectPath });

  const challenge = await s256Challenge(verifier);
  const params = new URLSearchParams({
    client_id: OIDC_CLIENT_ID,
    response_type: 'code',
    scope: 'openid profile email',
    redirect_uri: redirectUri(),
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    acr_values: OIDC_ACR_VALUE,
  });
  if (idpHint) params.set('kc_idp_hint', idpHint);
  return `${authorizeEndpoint()}?${params.toString()}`;
}

// ── token grants ──────────────────────────────────────────────────────────────

async function tokenGrant(params: Record<string, string>): Promise<OidcTokenSet> {
  const body = new URLSearchParams({ client_id: OIDC_CLIENT_ID, ...params });
  const response = await fetch(tokenEndpoint(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!response.ok) {
    throw new OidcError(response.status);
  }
  const json = (await response.json()) as {
    access_token?: string;
    refresh_token?: string;
    id_token?: string;
    expires_in?: number;
  };
  if (!json.access_token || !json.refresh_token) {
    throw new OidcError(response.status);
  }
  const expiresIn = typeof json.expires_in === 'number' ? json.expires_in : 60;
  return {
    accessToken: json.access_token,
    // Keycloak ROTATES refresh tokens — callers must always store THIS one.
    refreshToken: json.refresh_token,
    idToken: json.id_token ?? '',
    expiresAt: Date.now() + Math.max(expiresIn - OIDC_TOKEN_EXPIRY_SKEW_SECONDS, 0) * 1000,
  };
}

/** Exchange the authorization code (with the stored PKCE verifier). */
export function exchangeCode(code: string, verifier: string): Promise<OidcTokenSet> {
  return tokenGrant({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  });
}

/** Refresh grant — returns the ROTATED token set (store it verbatim). */
export function refreshTokens(refreshToken: string): Promise<OidcTokenSet> {
  return tokenGrant({ grant_type: 'refresh_token', refresh_token: refreshToken });
}

// ── end session ───────────────────────────────────────────────────────────────

/** Keycloak end-session URL (id_token_hint + back to /login on this origin). */
export function buildEndSessionUrl(idToken: string): string {
  const params = new URLSearchParams({
    client_id: OIDC_CLIENT_ID,
    post_logout_redirect_uri: `${window.location.origin}/login`,
  });
  if (idToken) params.set('id_token_hint', idToken);
  return `${endSessionEndpoint()}?${params.toString()}`;
}

// ── ID-token display claims ───────────────────────────────────────────────────

/**
 * Decode the ID-token payload (base64url JSON) for DISPLAY ONLY — header name,
 * email, initials. NEVER an authorization input: the api re-verifies every
 * request server-side (D-W4-DISPLAY-CLAIMS, rule #2).
 */
export function decodeIdTokenClaims(idToken: string): IdTokenDisplayClaims {
  const empty: IdTokenDisplayClaims = { name: null, email: null, preferredUsername: null };
  const payload = idToken.split('.')[1];
  if (!payload) return empty;
  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(normalized)) as Record<string, unknown>;
    return {
      name: typeof json['name'] === 'string' ? json['name'] : null,
      email: typeof json['email'] === 'string' ? json['email'] : null,
      preferredUsername:
        typeof json['preferred_username'] === 'string' ? json['preferred_username'] : null,
    };
  } catch {
    return empty;
  }
}
